import React, { useState, useEffect } from "react";
import { collection, addDoc, doc, setDoc, getDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "../styles/AdminPage.css";
import Part5Form from "./questionForms/Part5Form";

const AdminPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [section, setSection] = useState("reading"); // reading or listening
  const [selectedPart, setSelectedPart] = useState("part5");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // 기본 문제 상태
  const [baseQuestion, setBaseQuestion] = useState({
    section: "reading",
    part: "part5",
    category: "grammar",
    subCategory: "verb",
    text: "",
    choices: {
      A: "",
      B: "",
      C: "",
      D: "",
    },
    correctAnswer: "",
  });

  // 파트별 추가 필드
  const [audioFile, setAudioFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [passage, setPassage] = useState("");
  const [subQuestions, setSubQuestions] = useState([]);

  // Part1 전용 상태 수정
  const [part1Data, setPart1Data] = useState({
    category: "",
    subCategory: "", // action, grammar, similar
    questionNumber: "",
    choices: {
      A: "",
      B: "",
      C: "",
      D: "",
    },
    incorrectAnswers: [],
    explanation: "",
  });

  // 초기 상태 객체 수정
  const initialPart1Data = {
    category: "",
    subCategory: "",
    questionNumber: "",
    choices: {
      A: "",
      B: "",
      C: "",
      D: "",
    },
    incorrectAnswers: [],
    explanation: "",
  };

  // Part 2 전용 상태 추가
  const [part2Data, setPart2Data] = useState({
    subCategory: "", // auxiliary, tag, where 등
    questionNumber: "",
    questionText: "", // 질문 텍스트
    choices: {
      A: "",
      B: "",
      C: "",
    },
    correctAnswer: "", // 정답 (A, B, C 중 하나)
    incorrectAnswers: [], // 오답 2개 배열
    explanation: "", // 문제 해설
  });

  // Part 2 초기 상태
  const initialPart2Data = {
    subCategory: "",
    questionNumber: "",
    questionText: "",
    choices: {
      A: "",
      B: "",
      C: "",
    },
    correctAnswer: "",
    incorrectAnswers: [],
    explanation: "",
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.email === "dbsgpwls416@gmail.com") {
        setIsAuthorized(true);
      } else {
        setIsAuthorized(false);
        navigate("/");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handlePartChange = (part) => {
    setSelectedPart(part);
    // 파트에 따라 초기 상태 리셋
    setBaseQuestion((prev) => ({
      ...prev,
      part: part,
      choices:
        part === "part2"
          ? { A: "", B: "", C: "" }
          : { A: "", B: "", C: "", D: "" },
    }));
    setAudioFile(null);
    setImageFile(null);
    setPassage("");
    setSubQuestions([]);
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    // 필수 필드 검증
    if (!baseQuestion.text.trim()) {
      setErrorMessage("문제 내용을 입력해주세요.");
      setLoading(false);
      return;
    }

    // 모든 보기가 입력되었는지 확인
    const emptyChoices = Object.values(baseQuestion.choices).some(
      (choice) => !choice.trim()
    );
    if (emptyChoices) {
      setErrorMessage("모든 보기를 입력해주세요.");
      setLoading(false);
      return;
    }

    // 정답이 선택되었는지 확인
    if (!baseQuestion.correctAnswer) {
      setErrorMessage("정답을 선택해주세요.");
      setLoading(false);
      return;
    }

    try {
      // 경로 구성: section/part/category/subCategory/questions
      const path = `${baseQuestion.section}/${baseQuestion.part}/${baseQuestion.category}/${baseQuestion.subCategory}/questions`;
      const questionRef = collection(db, path);

      await addDoc(questionRef, {
        ...baseQuestion,
        createdAt: new Date(),
      });

      // 폼 초기화
      setBaseQuestion({
        section: section,
        part: selectedPart,
        category: "grammar",
        subCategory: "verb",
        text: "",
        choices: {
          A: "",
          B: "",
          C: "",
          D: "",
        },
        correctAnswer: "",
      });

      setSuccessMessage("문제가 성공적으로 저장되었습니다!");
    } catch (error) {
      console.error("Error adding document: ", error);
      setErrorMessage("문제 저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // Part1 오디오 업로드 핸들러
  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];

    if (!part1Data.questionNumber || !part1Data.category) {
      setErrorMessage("카테고리와 문제 번호를 먼저 입력해주세요.");
      return;
    }

    setLoading(true);
    try {
      const storage = getStorage();
      // 오디오 저장 경로 통일
      const filePath = `part1/${part1Data.category}/audio/question${part1Data.questionNumber}.mp3`;

      const audioRef = ref(storage, filePath);
      await uploadBytes(audioRef, file);
      const audioUrl = await getDownloadURL(audioRef);

      setPart1Data((prev) => ({
        ...prev,
        audioUrl: audioUrl,
      }));
    } catch (error) {
      console.error("❌ 오디오 업로드 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  // 유효성 검사 함수 수정
  const validatePart1Form = () => {
    const errors = [];

    // 필수 항목 체크
    if (!part1Data.category) errors.push("카테고리를 선택해주세요.");
    if (part1Data.category === "people" && !part1Data.subCategory) {
      errors.push("학습 유형을 선택해주세요.");
    }
    if (!part1Data.questionNumber) errors.push("문제 번호를 입력해주세요.");

    // 보기 체크
    const emptyChoices = Object.entries(part1Data.choices)
      .filter(([_, value]) => !value.trim())
      .map(([key]) => key);

    if (emptyChoices.length > 0) {
      errors.push(`보기 ${emptyChoices.join(", ")}를 입력해주세요.`);
    }

    // 틀린 답 3개 선택 체크
    if (part1Data.incorrectAnswers.length !== 3) {
      errors.push("틀린 보기를 3개 선택해주세요.");
    }

    // 설명 체크
    if (!part1Data.explanation) errors.push("문제 해설을 입력해주세요.");

    return errors;
  };

  const handlePart1Submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      let docRef;

      if (part1Data.category === "people") {
        // 사람 묘사의 경우 기존 로직 유지
        if (part1Data.subCategory === "action") {
          docRef = doc(
            db,
            "part1",
            "people",
            "questions",
            part1Data.questionNumber
          );
        } else {
          docRef = doc(
            db,
            "part1",
            "people",
            part1Data.subCategory,
            "data",
            "questions",
            part1Data.questionNumber
          );
        }
      } else if (part1Data.category === "location") {
        // 장소 묘사의 경우 수정된 경로 구조 사용
        docRef = doc(
          db,
          "part1",
          "location",
          "categories", // 중간 문서
          part1Data.subCategory,
          "questions", // 하위 컬렉션
          part1Data.questionNumber
        );
      }

      // 문서가 이미 존재하는지 확인
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        throw new Error(
          `${part1Data.questionNumber}번 문제가 이미 존재합니다.`
        );
      }

      const docData = {
        ...part1Data,
        imageUrl:
          part1Data.category === "location"
            ? `/images/part1/${part1Data.category}/${part1Data.subCategory}/${part1Data.questionNumber}.png`
            : `/images/part1/${part1Data.category}/${part1Data.questionNumber}.png`,
        audioUrl:
          part1Data.category === "location"
            ? `/audios/part1/${part1Data.category}/${part1Data.subCategory}/${part1Data.questionNumber}.mp3`
            : `/audios/part1/${part1Data.category}/${part1Data.questionNumber}.mp3`,
        createdAt: new Date(),
      };

      await setDoc(docRef, docData);

      setSuccessMessage(
        `문제 ${part1Data.questionNumber}번이 성공적으로 저장되었습니다!`
      );

      setPart1Data((prev) => ({
        ...initialPart1Data,
        category: prev.category,
        subCategory: prev.subCategory,
        questionNumber: String(Number(prev.questionNumber) + 1),
      }));
    } catch (error) {
      console.error("❌ 문제 저장 실패:", error);
      setErrorMessage(error.message || "문제 저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // Part 2 폼 유효성 검사
  const validatePart2Form = () => {
    const errors = [];

    if (!part2Data.subCategory) errors.push("문제 유형을 선택해주세요.");
    if (!part2Data.questionNumber) errors.push("문제 번호를 입력해주세요.");
    if (!part2Data.questionText.trim())
      errors.push("질문 내용을 입력해주세요.");

    // 보기 체크
    const emptyChoices = Object.entries(part2Data.choices)
      .filter(([_, value]) => !value.trim())
      .map(([key]) => key);

    if (emptyChoices.length > 0) {
      errors.push(`보기 ${emptyChoices.join(", ")}를 입력해주세요.`);
    }

    // 정답 체크
    if (!part2Data.correctAnswer) {
      errors.push("정답을 선택해주세요.");
    }

    // 오답 2개 선택 체크
    if (part2Data.incorrectAnswers.length !== 2) {
      errors.push("오답을 2개 선택해주세요.");
    }

    // 설명 체크
    if (!part2Data.explanation) errors.push("문제 해설을 입력해주세요.");

    return errors;
  };

  // Part 2 제출 핸들러 수정
  const handlePart2Submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    // 유효성 검사
    const errors = validatePart2Form();
    if (errors.length > 0) {
      setErrorMessage(errors.join("\n"));
      setLoading(false);
      return;
    }

    try {
      const docRef = doc(
        db,
        "part2",
        part2Data.subCategory,
        "questions",
        part2Data.questionNumber
      );

      // 문서가 이미 존재하는지 확인
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        throw new Error(
          `${part2Data.questionNumber}번 문제가 이미 존재합니다.`
        );
      }

      // audioUrl을 로컬 경로로 설정
      const docData = {
        ...part2Data,
        audioUrl: `/audios/part2/${part2Data.subCategory}/${part2Data.questionNumber}.mp3`,
        createdAt: new Date(),
      };

      await setDoc(docRef, docData);

      setSuccessMessage(
        `문제 ${part2Data.questionNumber}번이 성공적으로 저장되었습니다!\n
        오디오 파일명: /audios/part2/${part2Data.subCategory}/${part2Data.questionNumber}.mp3`
      );

      // 폼 초기화 (문제 번호는 1 증가)
      setPart2Data((prev) => ({
        ...initialPart2Data,
        subCategory: prev.subCategory,
        questionNumber: String(Number(prev.questionNumber) + 1),
      }));
    } catch (error) {
      console.error("❌ 문제 저장 실패:", error);
      setErrorMessage(error.message || "문제 저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 에러 메시지 스타일 수정
  const renderErrorMessage = () => {
    if (!errorMessage) return null;

    return (
      <div className="error-message">
        {errorMessage.split("\n").map((error, index) => (
          <p key={index}>❌ {error}</p>
        ))}
      </div>
    );
  };

  // Part1 폼 렌더링
  const renderPart1Form = () => {
    return (
      <div className="form-container">
        {/* 메시지 표시 영역 */}
        {successMessage && (
          <div className="message success-message">✅ {successMessage}</div>
        )}
        {errorMessage && (
          <div className="message error-message">❌ {errorMessage}</div>
        )}

        <form onSubmit={handlePart1Submit} className="question-form">
          <div className="form-group">
            <label>카테고리 선택:</label>
            <select
              value={part1Data.category}
              onChange={(e) =>
                setPart1Data((prev) => ({ ...prev, category: e.target.value }))
              }
              required
            >
              <option value="">카테고리 선택</option>
              <option value="people">사람 묘사</option>
              <option value="location">장소 묘사</option>
              <option value="object">사물 묘사</option>
              <option value="situation">상황 묘사</option>
            </select>
          </div>

          {part1Data.category === "people" && (
            <div className="form-group">
              <label>학습 유형 선택:</label>
              <select
                value={part1Data.subCategory}
                onChange={(e) =>
                  setPart1Data((prev) => ({
                    ...prev,
                    subCategory: e.target.value,
                  }))
                }
                required
              >
                <option value="">학습 유형 선택</option>
                <option value="action">동작 단어 훈련</option>
                <option value="grammar">문법 구조 학습</option>
              </select>
            </div>
          )}

          {part1Data.category === "location" && (
            <div className="form-group">
              <label>장소 유형 선택:</label>
              <select
                value={part1Data.subCategory}
                onChange={(e) =>
                  setPart1Data((prev) => ({
                    ...prev,
                    subCategory: e.target.value,
                  }))
                }
                required
              >
                <option value="">장소 유형 선택</option>
                <option value="workplace">업무 공간</option>
                <option value="commercial">상업 공간</option>
                <option value="educational">교육 공간</option>
                <option value="transportation">교통 관련</option>
                <option value="outdoor">야외 공간</option>
                <option value="medical">의료 시설</option>
                <option value="residential">주거 공간</option>
                <option value="cultural">문화 시설</option>
                <option value="sports">스포츠/레저 시설</option>
                <option value="industrial">산업 공간</option>
              </select>
            </div>
          )}

          <div className="form-group">
            <label>문제 번호:</label>
            <input
              type="number"
              value={part1Data.questionNumber}
              onChange={(e) =>
                setPart1Data((prev) => ({
                  ...prev,
                  questionNumber: e.target.value,
                }))
              }
              required
            />
          </div>

          <div className="form-group">
            <label>보기:</label>
            {Object.keys(part1Data.choices).map((choice) => (
              <div key={choice} className="choice-input">
                <label>{choice}:</label>
                <input
                  type="text"
                  value={part1Data.choices[choice]}
                  onChange={(e) =>
                    setPart1Data((prev) => ({
                      ...prev,
                      choices: {
                        ...prev.choices,
                        [choice]: e.target.value,
                      },
                    }))
                  }
                  required
                />
              </div>
            ))}
          </div>

          <div className="form-group">
            <label>틀린 보기 선택 (3개):</label>
            <div className="incorrect-answers-selection">
              {Object.keys(part1Data.choices).map((choice) => (
                <label key={choice}>
                  <input
                    type="checkbox"
                    checked={part1Data.incorrectAnswers.includes(choice)}
                    onChange={() => {
                      const newIncorrectAnswers =
                        part1Data.incorrectAnswers.includes(choice)
                          ? part1Data.incorrectAnswers.filter(
                              (answer) => answer !== choice
                            )
                          : [...part1Data.incorrectAnswers, choice];

                      if (newIncorrectAnswers.length <= 3) {
                        setPart1Data((prev) => ({
                          ...prev,
                          incorrectAnswers: newIncorrectAnswers,
                        }));
                      }
                    }}
                  />
                  {choice}. {part1Data.choices[choice]}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>오답 설명:</label>
            <textarea
              value={part1Data.explanation}
              onChange={(e) =>
                setPart1Data((prev) => ({
                  ...prev,
                  explanation: e.target.value,
                }))
              }
              placeholder="각 오답이 틀린 이유를 설명해주세요"
              rows={4}
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? "저장 중..." : "저장"}
          </button>
        </form>
      </div>
    );
  };

  // Part 2 폼 렌더링
  const renderPart2Form = () => {
    return (
      <div className="form-container">
        <form onSubmit={handlePart2Submit} className="question-form">
          <div className="form-group">
            <label>문제 유형 선택:</label>
            <select
              value={part2Data.subCategory}
              onChange={(e) =>
                setPart2Data((prev) => ({
                  ...prev,
                  subCategory: e.target.value,
                }))
              }
              required
            >
              <option value="">문제 유형 선택</option>
              <option value="auxiliary">조동사 의문문</option>
              <option value="tag">부가 의문문</option>
              <option value="where">장소 의문문</option>
              <option value="which">선택 의문문</option>
              <option value="who">인물 의문문</option>
              <option value="why">이유 의문문</option>
              <option value="nounClause">명사절 의문문</option>
              <option value="beVerb">Be동사 의문문</option>
              <option value="how">방법/상태 의문문</option>
              <option value="makeChoice">선택형 의문문</option>
              <option value="negative">부정 의문문</option>
              <option value="state">상태 의문문</option>
              <option value="what">What 의문문</option>
              <option value="when">시간 의문문</option>
              <option value="polite">공손한 의문문</option>
            </select>
          </div>

          <div className="form-group">
            <label>문제 번호:</label>
            <input
              type="number"
              value={part2Data.questionNumber}
              onChange={(e) =>
                setPart2Data((prev) => ({
                  ...prev,
                  questionNumber: e.target.value,
                }))
              }
              required
            />
          </div>

          <div className="form-group">
            <label>질문 내용:</label>
            <input
              type="text"
              value={part2Data.questionText}
              onChange={(e) =>
                setPart2Data((prev) => ({
                  ...prev,
                  questionText: e.target.value,
                }))
              }
              required
            />
          </div>

          <div className="form-group">
            <label>보기:</label>
            {Object.keys(part2Data.choices).map((choice) => (
              <div key={choice} className="choice-input">
                <label>{choice}:</label>
                <input
                  type="text"
                  value={part2Data.choices[choice]}
                  onChange={(e) =>
                    setPart2Data((prev) => ({
                      ...prev,
                      choices: {
                        ...prev.choices,
                        [choice]: e.target.value,
                      },
                    }))
                  }
                  required
                />
                <div className="answer-selection">
                  <label>
                    <input
                      type="radio"
                      name="correctAnswer"
                      value={choice}
                      checked={part2Data.correctAnswer === choice}
                      onChange={(e) =>
                        setPart2Data((prev) => ({
                          ...prev,
                          correctAnswer: e.target.value,
                          incorrectAnswers: prev.incorrectAnswers.filter(
                            (ans) => ans !== e.target.value
                          ),
                        }))
                      }
                    />
                    정답
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={part2Data.incorrectAnswers.includes(choice)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          if (part2Data.incorrectAnswers.length < 2) {
                            setPart2Data((prev) => ({
                              ...prev,
                              incorrectAnswers: [
                                ...prev.incorrectAnswers,
                                choice,
                              ],
                              correctAnswer:
                                prev.correctAnswer === choice
                                  ? ""
                                  : prev.correctAnswer,
                            }));
                          }
                        } else {
                          setPart2Data((prev) => ({
                            ...prev,
                            incorrectAnswers: prev.incorrectAnswers.filter(
                              (ans) => ans !== choice
                            ),
                          }));
                        }
                      }}
                    />
                    오답
                  </label>
                </div>
              </div>
            ))}
          </div>

          <div className="form-group">
            <label>문제 해설:</label>
            <textarea
              value={part2Data.explanation}
              onChange={(e) =>
                setPart2Data((prev) => ({
                  ...prev,
                  explanation: e.target.value,
                }))
              }
              required
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? "저장 중..." : "저장"}
          </button>
        </form>
      </div>
    );
  };

  if (!isAuthorized) {
    return <div>접근 권한이 없습니다.</div>;
  }

  return (
    <div className="admin-page">
      <h2>문제 관리</h2>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
      {errorMessage && <div className="error-message">{errorMessage}</div>}

      {/* 섹션 선택 */}
      <div className="section-selector">
        <button
          className={section === "listening" ? "active" : ""}
          onClick={() => setSection("listening")}
        >
          리스닝
        </button>
        <button
          className={section === "reading" ? "active" : ""}
          onClick={() => setSection("reading")}
        >
          리딩
        </button>
      </div>

      {/* 파트 선택 */}
      <div className="part-selector">
        {section === "listening" ? (
          <>
            <button
              className={selectedPart === "part1" ? "active" : ""}
              onClick={() => handlePartChange("part1")}
            >
              Part 1: 사진 묘사
            </button>
            <button
              className={selectedPart === "part2" ? "active" : ""}
              onClick={() => handlePartChange("part2")}
            >
              Part 2: 질의응답
            </button>
            <button
              className={selectedPart === "part3" ? "active" : ""}
              onClick={() => handlePartChange("part3")}
            >
              Part 3: 대화
            </button>
            <button
              className={selectedPart === "part4" ? "active" : ""}
              onClick={() => handlePartChange("part4")}
            >
              Part 4: 담화
            </button>
          </>
        ) : (
          <>
            <button
              className={selectedPart === "part5" ? "active" : ""}
              onClick={() => handlePartChange("part5")}
            >
              Part 5: 문법/어휘
            </button>
            <button
              className={selectedPart === "part6" ? "active" : ""}
              onClick={() => handlePartChange("part6")}
            >
              Part 6: 문장 완성
            </button>
            <button
              className={selectedPart === "part7" ? "active" : ""}
              onClick={() => handlePartChange("part7")}
            >
              Part 7: 독해
            </button>
          </>
        )}
      </div>

      {/* 파트별 입력 폼 */}
      <div className="question-form">
        {selectedPart === "part5" && (
          <Part5Form
            question={baseQuestion}
            setQuestion={setBaseQuestion}
            handleSubmit={handleSubmit}
            loading={loading}
          />
        )}
        {/* 여기에 다른 파트 폼들이 추가될 예정 */}
      </div>

      {/* Part1 문제 입력 폼 */}
      {selectedPart === "part1" && renderPart1Form()}
      {selectedPart === "part2" && renderPart2Form()}
    </div>
  );
};

// CSS 추가
const styles = `.form-container {
  position: relative;
  padding: 20px;
}

.message {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 25px;
  border-radius: 8px;
  animation: slideIn 0.3s ease-out;
  z-index: 1000;
}

.success-message {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.error-message {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
`;

// 스타일 태그 추가
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default AdminPage;
